import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatMedida, formatDateTime, formatDate } from "@/lib/format";
import { sharePdf } from "@/lib/share";
import {
  Search, Clock, Loader2, PackagePlus, Truck, Undo2, Ban, RotateCcw, Pencil,
  Trash2, RefreshCw, Share2, Printer, Tag, ChevronDown, ChevronUp, FileText,
  Eye, MoreHorizontal, GitMerge, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { RoloTimelineModal } from "@/components/RoloTimelineModal";
import { EntradaDetalheModal } from "@/components/EntradaDetalheModal";
import { RomaneioDetalheModal } from "@/components/RomaneioDetalheModal";

// Meta de cada tipo de evento (ícone / cor / rótulo / destaque)
const EV_META = {
  entrada:                          { label: "Entrada",                              icon: PackagePlus,  bg: "#F0FDF4", color: "#16A34A" },
  romaneio_criado:                  { label: "Romaneio",                             icon: Truck,        bg: "#EFF6FF", color: "#2563EB" },
  devolucao:                        { label: "Devolução",                            icon: Undo2,        bg: "#FEFCE8", color: "#CA8A04" },
  romaneio_cancelado:               { label: "Cancelou romaneio",                    icon: Ban,          bg: "#FEF2F2", color: "#DC2626", destaque: "danger" },
  estorno_rolo:                     { label: "Estorno de rolo",                      icon: RotateCcw,    bg: "#FFFBEB", color: "#B45309", destaque: "warning" },
  correcao_rolo:                    { label: "Correção de rolo",                     icon: Pencil,       bg: "#F5F3FF", color: "#7C3AED", destaque: "purple" },
  romaneio_status:                  { label: "Alterou status do romaneio",           icon: RefreshCw,    bg: "#EFF6FF", color: "#2563EB" },
  ent_compartilhado:                { label: "Compartilhou documento de entrada",    icon: Share2,       bg: "#F1F5F9", color: "#475569" },
  ent_impresso_doc:                 { label: "Imprimiu documento de entrada",        icon: Printer,      bg: "#F1F5F9", color: "#475569" },
  ent_impresso_etiquetas:           { label: "Imprimiu etiquetas do documento",      icon: Tag,          bg: "#F1F5F9", color: "#475569" },
  romaneio_compartilhado:           { label: "Compartilhou romaneio",                icon: Share2,       bg: "#F1F5F9", color: "#475569" },
  romaneio_impresso:                { label: "Imprimiu romaneio",                    icon: Printer,      bg: "#F1F5F9", color: "#475569" },
  estoque_relatorio_compartilhado:  { label: "Compartilhou relatório de estoque",    icon: Share2,       bg: "#F1F5F9", color: "#475569" },
  estoque_relatorio_impresso:       { label: "Imprimiu relatório de estoque",        icon: Printer,      bg: "#F1F5F9", color: "#475569" },
  etiqueta_reimpressa:              { label: "Reimpressão de etiqueta",              icon: Tag,          bg: "#F1F5F9", color: "#94a3b8" },
  gp_entrada:                       { label: "Entrada Gola e Punho",                 icon: PackagePlus,  bg: "#F0FDF4", color: "#16A34A" },
  gp_excluir:                       { label: "Exclusão Gola e Punho",                icon: Trash2,       bg: "#FEF2F2", color: "#DC2626" },
  gp_mesclar:                       { label: "Mesclagem Gola e Punho",               icon: GitMerge,     bg: "#EFF6FF", color: "#2563EB" },
  cor_criada:                       { label: "Cor cadastrada",                       icon: Palette,      bg: "#F0FDF4", color: "#16A34A" },
  cor_renomeada:                    { label: "Cor renomeada",                        icon: Palette,      bg: "#FFFBEB", color: "#B45309" },
  cor_excluida:                     { label: "Cor excluída",                         icon: Palette,      bg: "#FEF2F2", color: "#DC2626" },
  restore_realizado:                { label: "Restauração de backup",                icon: RotateCcw,    bg: "#EFF6FF", color: "#2563EB" },
};

const TIPOS_OPCOES = [
  { value: "", label: "Todos os tipos" },
  { value: "entrada", label: "Entrada" },
  { value: "romaneio_criado", label: "Romaneio" },
  { value: "devolucao", label: "Devolução" },
  { value: "romaneio_cancelado", label: "Cancelou romaneio" },
  { value: "estorno_rolo", label: "Estorno de rolo" },
  { value: "correcao_rolo", label: "Correção de rolo" },
  { value: "romaneio_status", label: "Alterou status do romaneio" },
  { value: "ent_compartilhado", label: "Compartilhou ENT" },
  { value: "ent_impresso_doc", label: "Imprimiu documento ENT" },
  { value: "ent_impresso_etiquetas", label: "Imprimiu etiquetas ENT" },
  { value: "romaneio_compartilhado", label: "Compartilhou romaneio" },
  { value: "romaneio_impresso", label: "Imprimiu romaneio" },
  { value: "estoque_relatorio_compartilhado", label: "Compartilhou relatório de estoque" },
  { value: "estoque_relatorio_impresso", label: "Imprimiu relatório de estoque" },
  { value: "etiqueta_reimpressa", label: "Reimpressão de etiqueta" },
];

const inputCls = "h-11 w-full rounded-md border-2 border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500";

export default function Historico() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rastreio, setRastreio] = useState("");
  const [rastreioLoading, setRastreioLoading] = useState(false);
  const [timelineRoloId, setTimelineRoloId] = useState(null);
  const [openEntradaId, setOpenEntradaId] = useState(null);
  const [openRomaneioId, setOpenRomaneioId] = useState(null);

  // Filtros
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [usuarioFiltro, setUsuarioFiltro] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [exporting, setExporting] = useState(false);

  // Detalhe expandido inline
  const [expandedId, setExpandedId] = useState(null);
  const [detalheCache, setDetalheCache] = useState({}); // { [id]: {rolos:[...]} }
  const [detalheLoading, setDetalheLoading] = useState(false);

  const load = () => {
    const params = { limit: 500 };
    if (dataDe) params.data_de = dataDe;
    if (dataAte) params.data_ate = dataAte;
    if (usuarioFiltro) params.usuario_id = usuarioFiltro;
    if (tipoFiltro) params.tipo = tipoFiltro;
    setLoading(true);
    api
      .get("/eventos", { params })
      .then((r) => setEventos(r.data))
      .catch((e) => { const _m = apiError(e); if (_m) toast.error(_m); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [dataDe, dataAte, usuarioFiltro, tipoFiltro]);

  const usuariosDisponiveis = useMemo(() => {
    const map = new Map();
    eventos.forEach((m) => {
      if (m.usuario_id && !map.has(m.usuario_id)) map.set(m.usuario_id, m.usuario_nome || m.usuario_id);
    });
    return Array.from(map.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1]), "pt-BR"));
  }, [eventos]);

  const buscarRastreio = async (e) => {
    e?.preventDefault?.();
    const code = rastreio.trim().toUpperCase();
    if (!code) return toast.error("Digite o código ou número do rolo (ex: 1572 ou ROLO-1572)");
    setRastreioLoading(true);
    try {
      const r = await api.get(`/rolos/codigo/${encodeURIComponent(code)}`);
      setTimelineRoloId(r.data.id);
    } catch (err) {
      const _m = apiError(err); if (_m) toast.error(_m);
    } finally {
      setRastreioLoading(false);
    }
  };

  const toggleExpand = async (ev) => {
    if (expandedId === ev.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(ev.id);
    if (detalheCache[ev.id]) return;
    setDetalheLoading(true);
    try {
      const r = await api.get(`/eventos/${ev.id}`);
      setDetalheCache((prev) => ({ ...prev, [ev.id]: r.data }));
    } catch (err) {
      const _m = apiError(err); if (_m) toast.error(_m);
    } finally {
      setDetalheLoading(false);
    }
  };

  const hoje = () => {
    const d = new Date();
    const ymd = d.toISOString().slice(0, 10);
    setDataDe(ymd);
    setDataAte(ymd);
  };
  const limparFiltros = () => {
    setDataDe(""); setDataAte(""); setUsuarioFiltro(""); setTipoFiltro("");
  };

  const exportarPdfQs = () => {
    const params = new URLSearchParams();
    if (dataDe) params.set("data_de", dataDe);
    if (dataAte) params.set("data_ate", dataAte);
    if (usuarioFiltro) params.set("usuario_id", usuarioFiltro);
    if (tipoFiltro) params.set("tipo", tipoFiltro);
    return params.toString();
  };
  const abrirPdfHistorico = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/eventos/pdf?${exportarPdfQs()}`, "_blank");
  };
  const compartilharPdfHistorico = async () => {
    setExporting(true);
    try {
      await sharePdf(`/eventos/pdf?${exportarPdfQs()}`, "jornal-de-eventos.pdf", "Jornal de Eventos");
    } finally {
      setExporting(false);
    }
  };

  const abrirDocumento = (ev) => {
    if (ev.documento_tipo === "entrada" && ev.documento_id) {
      setOpenEntradaId(ev.documento_id);
    } else if (ev.documento_tipo === "romaneio" && ev.documento_id) {
      setOpenRomaneioId(ev.documento_id);
    } else if (ev.documento_tipo === "rolo" && ev.documento_id) {
      setTimelineRoloId(ev.documento_id);
    }
  };

  return (
    <div data-testid="historico-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Histórico</h1>
        <p className="mt-1 text-sm text-slate-500">
          Jornal de eventos: 1 linha por ação (não por rolo). Clique numa linha para ver os rolos e ações do documento.
        </p>
      </div>

      {/* Rastreio por código de rolo */}
      <form
        onSubmit={buscarRastreio}
        className="mb-4 rounded-md border-2 border-slate-900 bg-slate-900 p-4 text-white"
        data-testid="historico-rastreio"
      >
        <div className="mb-2 flex items-center gap-2">
          <Clock size={16} />
          <span className="text-sm font-bold uppercase tracking-wide">Rastrear rolo</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="h-11 w-full rounded-md border-2 border-slate-700 bg-white/5 pl-9 pr-3 text-base text-white placeholder:text-slate-400 outline-none focus:border-blue-400"
              placeholder="Ex: 1572 ou ROLO-1572"
              value={rastreio}
              onChange={(e) => setRastreio(e.target.value)}
              data-testid="historico-rastreio-input"
            />
          </div>
          <button
            type="submit"
            disabled={rastreioLoading}
            data-testid="historico-rastreio-btn"
            className="flex h-11 items-center gap-2 rounded-md bg-blue-600 px-5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {rastreioLoading ? <Loader2 className="animate-spin" size={16} /> : <Clock size={16} />}
            Ver histórico
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Aceita só o número (ex: 1572) ou o código completo. Vê a linha do tempo: entrada, saída, devolução, correções.
        </p>
      </form>

      {/* Filtros do jornal */}
      <div className="mb-4 rounded-md border-2 border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Filtros</span>
          <button
            onClick={hoje}
            data-testid="filtro-hoje"
            className="rounded-md border-2 border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            Hoje
          </button>
          <button
            onClick={limparFiltros}
            data-testid="filtro-limpar"
            className="rounded-md border-2 border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            Limpar
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={abrirPdfHistorico}
              disabled={eventos.length === 0}
              data-testid="historico-exportar-pdf"
              className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <FileText size={14} /> PDF
            </button>
            <button
              onClick={compartilharPdfHistorico}
              disabled={exporting || eventos.length === 0}
              data-testid="historico-exportar-share"
              className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              Compartilhar
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">De</label>
            <input type="date" className={inputCls} value={dataDe} onChange={(e) => setDataDe(e.target.value)} data-testid="filtro-data-de" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Até</label>
            <input type="date" className={inputCls} value={dataAte} onChange={(e) => setDataAte(e.target.value)} data-testid="filtro-data-ate" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Usuário</label>
            <select value={usuarioFiltro} onChange={(e) => setUsuarioFiltro(e.target.value)} className={inputCls} data-testid="filtro-usuario">
              <option value="">Todos os usuários</option>
              {usuariosDisponiveis.map(([id, nome]) => (
                <option key={id} value={id}>{nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Tipo de evento</label>
            <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} className={inputCls} data-testid="filtro-tipo">
              {TIPOS_OPCOES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400">
          <Loader2 className="animate-spin" />
        </div>
      ) : eventos.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="historico-empty">
          Nenhum evento no período selecionado.
        </div>
      ) : (
        <div className="space-y-2" data-testid="jornal-list">
          {eventos.map((ev) => {
            const meta = EV_META[ev.tipo] || { label: ev.tipo, icon: Clock, bg: "#F1F5F9", color: "#475569" };
            const isOpen = expandedId === ev.id;
            const destaqueCls = meta.destaque === "danger"
              ? "border-red-300"
              : meta.destaque === "warning"
                ? "border-amber-300"
                : meta.destaque === "purple"
                  ? "border-purple-300"
                  : "border-slate-200";
            const detalhe = detalheCache[ev.id];
            return (
              <div
                key={ev.id}
                className={`rounded-md border-2 bg-white ${destaqueCls}`}
                data-testid={`ev-${ev.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(ev)}
                  data-testid={`ev-toggle-${ev.id}`}
                  className="flex w-full items-start gap-3 p-4 text-left hover:bg-slate-50"
                >
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    <meta.icon size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="rounded-sm px-2 py-0.5 text-xs font-bold uppercase"
                        style={{ background: meta.bg, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      {ev.documento_codigo && (
                        <span className="font-mono font-semibold text-slate-900">{ev.documento_codigo}</span>
                      )}
                      {ev.ref && (
                        <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                          REF {ev.ref}
                        </span>
                      )}
                      {ev.qtd_rolos > 0 && (
                        <span className="text-sm text-slate-600">
                          {ev.qtd_rolos} {ev.qtd_rolos === 1 ? "rolo" : "rolos"}
                        </span>
                      )}
                    </div>
                    {ev.detalhe && <div className="mt-0.5 text-sm text-slate-500">{ev.detalhe}</div>}
                    <div className="mt-0.5 text-xs text-slate-400">
                      {formatDateTime(ev.data_hora)} · por {ev.usuario_nome || "—"}
                    </div>
                  </div>
                  <div className="shrink-0 text-slate-400">
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t-2 border-slate-100 p-4">
                    {detalheLoading && !detalhe && (
                      <div className="flex justify-center py-6 text-slate-400"><Loader2 className="animate-spin" /></div>
                    )}
                    {detalhe && (
                      <>
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          {ev.documento_tipo && ev.documento_id && (
                            <button
                              type="button"
                              onClick={() => abrirDocumento(ev)}
                              data-testid={`ev-abrir-doc-${ev.id}`}
                              className="flex items-center gap-1 rounded-md border-2 border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                            >
                              <Eye size={14} />
                              {ev.documento_tipo === "romaneio" ? "Ver romaneio" : ev.documento_tipo === "entrada" ? "Ver documento" : "Ver rolo"}
                            </button>
                          )}
                        </div>
                        {(detalhe.rolos && detalhe.rolos.length > 0) ? (
                          <div className="overflow-x-auto rounded-md border border-slate-200">
                            <table className="w-full text-left text-sm">
                              <thead className="bg-slate-50">
                                <tr>
                                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Rolo</th>
                                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Tecido</th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-500">Peso</th>
                                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-500">Status</th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-500"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {detalhe.rolos.map((r) => (
                                  <tr key={r.id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 font-mono font-bold text-slate-900">{r.codigo}</td>
                                    <td className="px-3 py-2 text-slate-600">{r.tipo} {r.cor}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-800">{formatMedida(r.peso_atual_kg, r.unidade)}</td>
                                    <td className="px-3 py-2 text-slate-600">{r.status}</td>
                                    <td className="px-3 py-2 text-right">
                                      <button
                                        type="button"
                                        onClick={() => setTimelineRoloId(r.id)}
                                        data-testid={`ev-rolo-hist-${r.codigo}`}
                                        className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                                      >
                                        Rastrear
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="rounded-md border-2 border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">
                            Este evento não tem rolos detalhados.
                          </div>
                        )}
                        <div className="mt-3 text-xs text-slate-500">
                          {formatDate(ev.data_hora)} · por {ev.usuario_nome || "—"}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <RoloTimelineModal
        roloId={timelineRoloId}
        open={!!timelineRoloId}
        onClose={() => setTimelineRoloId(null)}
        onOpenEntrada={(id) => setOpenEntradaId(id)}
        onOpenRomaneio={(rom) => setOpenRomaneioId(rom.id)}
      />
      <EntradaDetalheModal
        entradaId={openEntradaId}
        open={!!openEntradaId}
        onClose={() => setOpenEntradaId(null)}
      />
      <RomaneioDetalheModal
        romaneioId={openRomaneioId}
        open={!!openRomaneioId}
        onClose={() => setOpenRomaneioId(null)}
        zClass="z-[60]"
      />
    </div>
  );
}
